import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Assignment as TaskIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  PriorityHigh as PriorityIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Label as LabelIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CompleteIcon,
  Block as BlockIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Pause as PauseIcon,
} from "@mui/icons-material";
import {
  fetchTasks,
  deleteExistingTask,
  updateExistingTaskStatus,
} from "store/features/company/projectManagement/task/taskActions";
import {
  selectTasks,
  selectTaskLoading,
  selectTaskDeleting,
  selectTaskUpdatingStatus,
} from "store/features/company/projectManagement/task/taskSelectors";
import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import {
  selectProjects,
  selectProjectLoading,
} from "store/features/company/projectManagement/project/projectSelectors";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import {
  selectEmployees,
  selectEmployeeLoading,
} from "store/features/company/employee/employeeSelectors";
import AddTaskModal from "./AddTaskModal";
import EditTaskModal from "./EditTaskModal";
import taskSvg from "assets/folders/task.svg";

// Import card background images
import cardBg1 from "assets/task-card/1.jpg";
import cardBg2 from "assets/task-card/2.jpg";

const Task = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const tasks = useSelector(selectTasks);
  const loading = useSelector(selectTaskLoading);
  const updatingStatus = useSelector(selectTaskUpdatingStatus);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTaskForStatus, setSelectedTaskForStatus] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const projects = useSelector(selectProjects);
  const employees = useSelector(selectEmployees);

  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchEmployees());
  }, [dispatch]);

  useEffect(() => {
    const params = {};
    if (projectFilter) params.project_id = projectFilter;
    if (assignedToFilter) params.assigned_to = assignedToFilter;
    dispatch(fetchTasks(Object.keys(params).length ? params : null));
  }, [dispatch, projectFilter, assignedToFilter]);

  const handleStatusMenuOpen = (event, task) => {
    setAnchorEl(event.currentTarget);
    setSelectedTaskForStatus(task);
  };

  const handleStatusMenuClose = () => {
    setAnchorEl(null);
    setSelectedTaskForStatus(null);
  };

  const handleStatusChange = async (newStatus) => {
    if (selectedTaskForStatus) {
      const result = await dispatch(
        updateExistingTaskStatus(selectedTaskForStatus.id, {
          status: newStatus,
        }),
      );
      if (result.success) {
        dispatch(fetchTasks());
      }
    }
    handleStatusMenuClose();
  };

  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (taskToDelete) {
      setDeleting(true);
      const result = await dispatch(deleteExistingTask(taskToDelete.id));
      if (result.success) {
        dispatch(fetchTasks());
        setDeleteDialogOpen(false);
        setTaskToDelete(null);
      }
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    return project ? project.name : `Project ${projectId}`;
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find((e) => e.id === employeeId);
    return employee
      ? `${employee.first_name} ${employee.last_name}`
      : `Employee ${employeeId}`;
  };

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch =
      searchTerm === "" ||
      task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || task.status === statusFilter;

    const matchesPriority =
      priorityFilter === "all" || task.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const formatDateShort = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    return `${day}/${month}/${String(year).slice(-2)}`;
  };

  const getPriorityIcon = (priority) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return <PriorityIcon sx={{ fontSize: 16, color: "#dc2626" }} />;
      case "high":
        return <TrendingUpIcon sx={{ fontSize: 16, color: "#f97316" }} />;
      case "medium":
        return <TrendingFlatIcon sx={{ fontSize: 16, color: "#eab308" }} />;
      case "low":
        return <TrendingDownIcon sx={{ fontSize: 16, color: "#22c55e" }} />;
      default:
        return <PriorityIcon sx={{ fontSize: 16, color: "#9ca3af" }} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "#dc2626";
      case "high":
        return "#f97316";
      case "medium":
        return "#eab308";
      case "low":
        return "#22c55e";
      default:
        return "#9ca3af";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "todo":
        return <ScheduleIcon sx={{ fontSize: 16 }} />;
      case "in_progress":
        return <PlayIcon sx={{ fontSize: 16 }} />;
      case "review":
        return <PriorityIcon sx={{ fontSize: 16 }} />;
      case "done":
        return <CompleteIcon sx={{ fontSize: 16 }} />;
      case "paused":
        return <PauseIcon sx={{ fontSize: 16 }} />;
      case "blocked":
        return <BlockIcon sx={{ fontSize: 16 }} />;
      default:
        return <TaskIcon sx={{ fontSize: 16 }} />;
    }
  };

  // Get alternating background image for cards
  const getCardBgImage = (index) => {
    return index % 2 === 0 ? cardBg1 : cardBg2;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={taskSvg}
              alt="Tasks"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                }}
              >
                Tasks
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  mt: 0.5,
                }}
              >
                Manage your project tasks
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Task
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          placeholder="Search by title or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 0.7,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="todo">To Do</MenuItem>
          <MenuItem value="in_progress">In Progress</MenuItem>
          <MenuItem value="review">Review</MenuItem>
          <MenuItem value="paused">Paused</MenuItem>
          <MenuItem value="done">Done</MenuItem>
          <MenuItem value="blocked">Blocked</MenuItem>
        </TextField>

        <TextField
          select
          label="Priority"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Priorities</MenuItem>
          <MenuItem value="urgent">Urgent</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="low">Low</MenuItem>
        </TextField>

        <TextField
          select
          label="Project"
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          {projects.map((project) => (
            <MenuItem key={project.id} value={project.id}>
              {project.name} ({project.code})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Assigned To"
          value={assignedToFilter}
          onChange={(e) => setAssignedToFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          {employees.map((emp) => (
            <MenuItem key={emp.id} value={emp.id}>
              {emp.first_name} {emp.last_name} ({emp.employee_code})
            </MenuItem>
          ))}
        </TextField>

        {(searchTerm ||
          statusFilter !== "all" ||
          priorityFilter !== "all" ||
          projectFilter ||
          assignedToFilter) && (
          <Button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setPriorityFilter("all");
              setProjectFilter("");
              setAssignedToFilter("");
            }}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: "8px",
              color: "#f59e0b",
              borderColor: "#f59e0b",
              whiteSpace: "nowrap",
            }}
          >
            Clear Filters
          </Button>
        )}
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: "#f59e0b" }} />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              }}
            >
              Loading tasks...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <>
          {/* Task Cards Grid */}
          {filteredTasks.length === 0 ? (
            <Paper
              sx={{
                p: 6,
                textAlign: "center",
                borderRadius: "16px",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              }}
            >
              <TaskIcon sx={{ fontSize: 64, color: "#d1d5db", mb: 2 }} />
              <Typography
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                }}
              >
                No tasks found. Click "Add Task" to get started.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
              <Grid container spacing={3}>
                {filteredTasks.map((task, index) => {
                  const cardColors = task.status;
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      md={4}
                      lg={4}
                      xl={4}
                      key={task.id}
                      sx={{ maxWidth: "30%", flexBasis: "30%" }}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        style={{ height: "100%" }}
                      >
                        <Card
                          sx={{
                            position: "relative",
                            borderRadius: "20px",
                            height: "100%",
                            minHeight: "380px",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            background: (() => {
                              if (theme.palette.mode === "dark") {
                                switch (task.status?.toLowerCase()) {
                                  case "todo":
                                    return "linear-gradient(135deg, #1a1817 0%, #23201d 100%)";
                                  case "in_progress":
                                    return "linear-gradient(135deg, #171c24 0%, #1a2330 100%)";
                                  case "review":
                                    return "linear-gradient(135deg, #1c1820 0%, #241d30 100%)";
                                  case "done":
                                    return "linear-gradient(135deg, #172018 0%, #1a2a1f 100%)";
                                  case "paused":
                                    return "linear-gradient(135deg, #1a1817 0%, #23201d 100%)";
                                  case "blocked":
                                    return "linear-gradient(135deg, #201818 0%, #2a1d1d 100%)";
                                  default:
                                    return "linear-gradient(135deg, #171c24 0%, #1a2330 100%)";
                                }
                              }
                              switch (task.status?.toLowerCase()) {
                                case "todo":
                                  return "linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)";
                                case "in_progress":
                                  return "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)";
                                case "review":
                                  return "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)";
                                case "done":
                                  return "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)";
                                case "paused":
                                  return "linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)";
                                case "blocked":
                                  return "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)";
                                default:
                                  return "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
                              }
                            })(),

                            transition: "all 0.3s ease-in-out",
                            boxShadow: theme.shadows[4],
                            "&:hover": {
                              transform: "translateY(-8px)",
                              boxShadow: theme.shadows[12],
                            },

                            // Diagonal corner image overlay
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              bottom: 0,
                              right: 0,
                              width: "180px",
                              height: "180px",
                              backgroundImage: `url(${getCardBgImage(index)})`,
                              backgroundSize: "contain",
                              backgroundPosition: "center",
                              opacity:
                                theme.palette.mode === "dark" ? 0.08 : 0.25,
                              filter:
                                theme.palette.mode === "dark"
                                  ? "brightness(0.8) contrast(1.2)"
                                  : "none",
                              maskImage:
                                "linear-gradient(135deg, transparent 0%, transparent 40%, black 60%, black 100%)",
                              WebkitMaskImage:
                                "linear-gradient(135deg, transparent 0%, transparent 40%, black 60%, black 100%)",
                              zIndex: 0,
                              pointerEvents: "none",
                            },
                          }}
                        >
                          {/* Subtle orange/yellow gradient overlay */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background:
                                "linear-gradient(135deg, rgba(245,158,11,0.05) 0%, rgba(217,119,6,0.02) 100%)",
                              zIndex: 0,
                              pointerEvents: "none",
                            }}
                          />

                          <CardContent
                            sx={{
                              flexGrow: 1,
                              position: "relative",
                              zIndex: 1,
                              p: 2.5,
                            }}
                          >
                            {/* Header with Priority and Status */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              {/* Priority - Left side */}
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  border: `1px solid ${getPriorityColor(
                                    task.priority,
                                  )}`,
                                  borderRadius: "4px",
                                  padding: "2px 8px",
                                  height: "28px",
                                }}
                              >
                                {getPriorityIcon(task.priority)}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: "bold",
                                    color: getPriorityColor(task.priority),
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {task.priority?.toUpperCase() || "N/A"}
                                </Typography>
                              </Box>

                              {/* Status - Right side */}
                              <Chip
                                icon={getStatusIcon(task.status)}
                                label={getStatusLabel(task.status)}
                                size="small"
                                onClick={(e) => handleStatusMenuOpen(e, task)}
                                variant="outlined"
                                sx={{
                                  borderColor: getStatusColor(task.status)
                                    .color,
                                  color: getStatusColor(task.status).color,
                                  fontWeight: "bold",
                                  fontSize: "0.7rem",
                                  height: "28px",
                                  cursor: "pointer",
                                  "& .MuiChip-icon": {
                                    color: getStatusColor(task.status).color,
                                    fontSize: "14px",
                                  },
                                  "&:hover": {
                                    backgroundColor: getStatusColor(task.status)
                                      .bg,
                                    opacity: 0.8,
                                  },
                                }}
                              />
                            </Box>

                            {/* Title and ID */}
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  border: `1.5px solid #f59e0b`,
                                  backgroundColor: "transparent",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.7rem",
                                    fontWeight: "600",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#f59e0b"
                                        : "#d97706",
                                  }}
                                >
                                  #{task.id}
                                </Typography>
                              </Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: "1rem",
                                  fontFamily: "monospace",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#f59e0b"
                                      : "#d97706",
                                  lineHeight: 1.3,
                                  flex: 1,
                                }}
                              >
                                {task.title}
                              </Typography>
                            </Stack>

                            {/* Description */}
                            {task.description && (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontStyle: "italic",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#9ca3af"
                                      : "#6b7280",
                                  mb: 2,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: 1.4,
                                }}
                              >
                                {task.description}
                              </Typography>
                            )}

                            {/* Divider */}
                            <Box
                              sx={{
                                borderTop: `1px solid ${
                                  theme.palette.mode === "dark"
                                    ? "#f0a00ccc"
                                    : "#eda517"
                                }`,
                                my: 1.5,
                              }}
                            />

                            {/* Task Details */}
                            <Stack spacing={1.5}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <BusinessIcon
                                  sx={{
                                    fontSize: 14,
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#eb0ac6"
                                        : "#ae0291",
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#ede0e0"
                                        : "#3b3e44",
                                    fontSize: "0.7rem",
                                    minWidth: "85px",
                                  }}
                                >
                                  Project:
                                </Typography>
                                <Typography
                                  sx={{
                                    fontStyle: "italic",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#d1d5db"
                                        : "#4b5563",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {getProjectName(task.project_id)} (ID:{" "}
                                  {task.project_id})
                                </Typography>
                              </Stack>

                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <PersonIcon
                                  sx={{ fontSize: 14, color: "#f59e0b" }}
                                />
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#ede0e0"
                                        : "#3b3e44",
                                    fontSize: "0.7rem",
                                    minWidth: "85px",
                                  }}
                                >
                                  Assigned:
                                </Typography>
                                <Typography
                                  sx={{
                                    fontStyle: "italic",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#d1d5db"
                                        : "#4b5563",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {getEmployeeName(task.assigned_to)} (ID:{" "}
                                  {task.assigned_to})
                                </Typography>
                              </Stack>

                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <PersonIcon
                                  sx={{
                                    fontSize: 14,
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#3a85f7"
                                        : "#1553af",
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#ede0e0"
                                        : "#3b3e44",
                                    fontSize: "0.7rem",
                                    minWidth: "85px",
                                  }}
                                >
                                  Created by:
                                </Typography>
                                <Typography
                                  sx={{
                                    fontStyle: "italic",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#d1d5db"
                                        : "#4b5563",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {getEmployeeName(task.created_by)} (ID:{" "}
                                  {task.created_by})
                                </Typography>
                              </Stack>

                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <EventIcon
                                  sx={{ fontSize: 14, color: "#10b981" }}
                                />
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#ede0e0"
                                        : "#3b3e44",
                                    fontSize: "0.7rem",
                                    minWidth: "85px",
                                  }}
                                >
                                  Timeline:
                                </Typography>
                                <Typography
                                  sx={{
                                    fontStyle: "italic",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#d1d5db"
                                        : "#4b5563",
                                    fontSize: "0.75rem",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {formatDateShort(task.start_date)} -{" "}
                                  {formatDateShort(task.due_date)}
                                </Typography>
                              </Stack>

                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <AccessTimeIcon
                                  sx={{ fontSize: 14, color: "#f59e0b" }}
                                />
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#ede0e0"
                                        : "#3b3e44",
                                    fontSize: "0.7rem",
                                    minWidth: "85px",
                                  }}
                                >
                                  Hours:
                                </Typography>
                                <Typography
                                  sx={{
                                    fontStyle: "italic",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#d1d5db"
                                        : "#4b5563",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  Est: {task.estimated_hours}h | Actual:{" "}
                                  {task.actual_hours}h
                                </Typography>
                              </Stack>

                              {/* Labels */}
                              {task.labels && task.labels.length > 0 && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  flexWrap="wrap"
                                  mt={0.5}
                                >
                                  {task.labels.map((label, idx) => (
                                    <Chip
                                      key={idx}
                                      label={label}
                                      size="small"
                                      icon={<LabelIcon />}
                                      sx={{
                                        backgroundColor: "transparent",
                                        color:
                                          theme.palette.mode === "dark"
                                            ? "#20b1ff"
                                            : "#097ec1",
                                        fontStyle: "italic",
                                        height: "20px",
                                        fontSize: "0.65rem",
                                        border: "none",
                                        "& .MuiChip-icon": {
                                          color:
                                            theme.palette.mode === "dark"
                                              ? "#20b1ff"
                                              : "#097ec1",
                                          fontSize: "14px",
                                        },
                                      }}
                                    />
                                  ))}
                                </Stack>
                              )}
                            </Stack>
                          </CardContent>

                          <CardActions
                            sx={{
                              p: 2,
                              pt: 0,
                              justifyContent: "flex-end",
                              position: "relative",
                              zIndex: 1,
                            }}
                          >
                            <IconButton
                              onClick={() => {
                                setSelectedTask(task);
                                setShowEditModal(true);
                              }}
                              size="small"
                              sx={{
                                color: "#f59e0b",
                                "&:hover": {
                                  backgroundColor: "rgba(245,158,11,0.1)",
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteClick(task)}
                              disabled={deleting}
                              size="small"
                              sx={{
                                color: "#ef4444",
                                "&:hover": {
                                  backgroundColor: "rgba(239,68,68,0.1)",
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </CardActions>
                        </Card>
                      </motion.div>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Count Summary */}
          {!loading && tasks.length > 0 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 3 }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                }}
              >
                Total Tasks: {tasks.length}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                }}
              >
                Showing: {filteredTasks.length} of {tasks.length}
              </Typography>
            </Stack>
          )}
        </>
      )}

      {/* Status Change Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleStatusMenuClose}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: 150,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
          },
        }}
      >
        <MenuItem onClick={() => handleStatusChange("todo")}>
          <ScheduleIcon sx={{ fontSize: 18, mr: 1, color: "#92400e" }} />
          To Do
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange("in_progress")}>
          <PlayIcon sx={{ fontSize: 18, mr: 1, color: "#1e40af" }} />
          In Progress
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange("review")}>
          <PriorityIcon sx={{ fontSize: 18, mr: 1, color: "#6b21a5" }} />
          Review
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange("paused")}>
          <PauseIcon sx={{ fontSize: 18, mr: 1, color: "#065f46" }} />
          Paused
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange("done")}>
          <CompleteIcon sx={{ fontSize: 18, mr: 1, color: "#53ff24" }} />
          Done
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange("blocked")}>
          <BlockIcon sx={{ fontSize: 18, mr: 1, color: "#381010" }} />
          Blocked
        </MenuItem>
      </Menu>

      {/* Modals */}
      <AddTaskModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchTasks())}
      />

      <EditTaskModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onSuccess={() => dispatch(fetchTasks())}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            Delete Task
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 1 }}>
            Are you sure you want to delete
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#f59e0b",
              textAlign: "center",
              mb: 2,
            }}
          >
            "{taskToDelete?.title}"
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};

// Helper functions
const getStatusLabel = (status) => {
  switch (status?.toLowerCase()) {
    case "todo":
      return "To Do";
    case "in_progress":
      return "In Progress";
    case "review":
      return "Review";
    case "done":
      return "Done";
    case "paused":
      return "Paused";
    case "blocked":
      return "Blocked";
    default:
      return status || "N/A";
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "todo":
      return { bg: "#fef3c7", color: "#92400e" };
    case "in_progress":
      return { bg: "#dbeafe", color: "#1e40af" };
    case "review":
      return { bg: "#f3e8ff", color: "#6b21a5" };
    case "done":
      return { bg: "#d1fae5", color: "#065f46" };
    case "paused":
      return { bg: "#fef3c7", color: "#92400e" };
    case "blocked":
      return { bg: "#fee2e2", color: "#991b1b" };
    default:
      return { bg: "#e5e7eb", color: "#374151" };
  }
};

export default Task;
